import { useRef, useState } from 'react'

import * as authService from 'services/auth'
import { useAuth } from 'hooks/useAuth'
import { trackEvent, AUTH_EVENTS } from 'services/mixpanel'
import type { AuthResponse, SendOtpResponse } from 'models/response'
import { parseFutureDate } from 'utils/invite-accept'
import { trueErrorMessage } from 'utils/error'
import {
	CODE_EXPIRED_MESSAGE,
	CODE_LOCKED_MESSAGE,
	CODE_RESENT_MESSAGE,
	START_RATE_LIMITED_MESSAGE,
	VERIFY_RATE_LIMITED_MESSAGE,
	interpretEmailStartError,
	interpretEmailVerifyError,
	invalidCodeMessage,
} from 'utils/email-code-sign-in'

export type EmailCodeMessage = { type: 'danger' | 'success' | 'info'; description: string }

/** What a right code can still run into: no account uses the email. The code is spent. */
export type EmailCodeOutcome = { kind: 'noAccount' }

interface Options {
	isModal?: boolean
	/** Entered through "Forgot password?": same flow, tracked separately. */
	isForgotPassword?: boolean
	/** Shared with the rest of the sign-in form, so one alert shows every step's feedback. */
	setMessage: (message: EmailCodeMessage | null) => void
	onSignedIn?: (session: AuthResponse) => void
}

/** The resend throttle (429_User_002) sends no Retry-After; the API's resend gap is about this long. */
const DEFAULT_THROTTLE_COOLDOWN_MS = 30_000

const isoOrNull = (value: unknown): string | null => parseFutureDate(value)?.toISOString() ?? null

const laterOf = (a: string | null, b: string | null): string | null => {
	if (!a || !b) return a ?? b
	return new Date(a).getTime() >= new Date(b).getTime() ? a : b
}

/** `POST /auth/email/start` + `/auth/email/verify`: passwordless sign-in (and forgot password) by emailed code. */
export function useEmailCodeSignIn({ isModal, isForgotPassword = false, setMessage, onSignedIn }: Options) {
	const { emailCodeVerify } = useAuth()
	// The address the live code was sent to. A code is bound to it, so changing the email always means a new start.
	const [email, setEmail] = useState<string | null>(null)
	const [nextResend, setNextResend] = useState<string | null>(null)
	const [blockedUntil, setBlockedUntil] = useState<string | null>(null)
	// 429 Retry-After on start, shown as a countdown on the button that calls it
	const [retryAt, setRetryAt] = useState<string | null>(null)
	const [codeExpired, setCodeExpired] = useState(false)
	const [outcome, setOutcome] = useState<EmailCodeOutcome | null>(null)
	const [sending, setSending] = useState(false)
	const [verifying, setVerifying] = useState(false)
	// Bumped to clear the code input after a refused code
	const [resetKey, setResetKey] = useState(0)
	// One request at a time. A ref, because state only flips on the next render and an auto-submit or a double
	// click can land before it.
	const inFlightRef = useRef(false)

	const applySendResult = (emailAddress: string, rs: SendOtpResponse) => {
		setEmail(emailAddress)
		setNextResend(isoOrNull(rs.nextResend))
		setBlockedUntil(isoOrNull(rs.blockedUntil))
		setRetryAt(null)
		setCodeExpired(false)
		setOutcome(null)
		setResetKey(k => k + 1)
	}

	/** Sends a code to `emailAddress` (a resend when it is the current one). Resolves true once a code is on its way. */
	const start = async (emailAddress: string, turnstileToken?: string): Promise<boolean> => {
		if (inFlightRef.current) return false
		inFlightRef.current = true
		const trimmed = emailAddress.trim()
		const isResend = trimmed === email
		setSending(true)
		setMessage(null)
		if (!isResend) {
			trackEvent(AUTH_EVENTS.SIGN_IN_ATTEMPTED, { method: 'email_code' })
			if (isForgotPassword) trackEvent(AUTH_EVENTS.FORGOT_PASSWORD_CODE_STARTED)
		}
		try {
			// The api instance resolves with the response body
			const rs = (await authService.emailStart(
				{ emailAddress: trimmed },
				turnstileToken
			)) as unknown as SendOtpResponse
			// Anything but a SendOtpResult (e.g. the app's HTML from a misrouted proxy) must not read as a throttle
			if (typeof rs?.isSuccess !== 'boolean') {
				setMessage({ type: 'danger', description: trueErrorMessage(undefined) })
				return false
			}
			if (!rs.isSuccess) {
				setMessage({ type: 'danger', description: START_RATE_LIMITED_MESSAGE })
				return false
			}
			applySendResult(trimmed, rs)
			if (isResend) setMessage({ type: 'success', description: CODE_RESENT_MESSAGE })
			trackEvent(AUTH_EVENTS.EMAIL_CODE_START_SENT, { is_resend: isResend, is_forgot_password: isForgotPassword })
			return true
		} catch (error) {
			const failure = interpretEmailStartError(error)
			trackEvent(AUTH_EVENTS.SIGN_IN_FAILED, { method: 'email_code', step: 'start', reason: failure.kind })
			if (failure.kind === 'rateLimited') {
				setRetryAt((failure.retryAt ?? new Date(Date.now() + DEFAULT_THROTTLE_COOLDOWN_MS)).toISOString())
				setMessage({ type: 'danger', description: START_RATE_LIMITED_MESSAGE })
			} else {
				setMessage({ type: 'danger', description: failure.message })
			}
			return false
		} finally {
			inFlightRef.current = false
			setSending(false)
		}
	}

	const verify = (code: string) => {
		if (!email || inFlightRef.current || parseFutureDate(blockedUntil)) return
		inFlightRef.current = true
		setVerifying(true)
		setMessage(null)
		emailCodeVerify(
			{ emailAddress: email, code },
			(session: AuthResponse) => {
				inFlightRef.current = false
				setVerifying(false)
				trackEvent(AUTH_EVENTS.EMAIL_CODE_VERIFIED, { is_complete: session.isComplete })
				onSignedIn?.(session)
			},
			(error: unknown) => {
				inFlightRef.current = false
				setVerifying(false)
				setResetKey(k => k + 1)
				const failure = interpretEmailVerifyError(error)
				if (failure.kind === 'noAccount') trackEvent(AUTH_EVENTS.EMAIL_CODE_NO_ACCOUNT)
				else trackEvent(AUTH_EVENTS.EMAIL_CODE_FAILED, { reason: failure.kind })
				switch (failure.kind) {
					case 'noAccount':
						setOutcome({ kind: 'noAccount' })
						return
					case 'wrong':
						setMessage({ type: 'danger', description: invalidCodeMessage(failure.attemptsLeft) })
						return
					case 'locked':
						setBlockedUntil(failure.blockedUntil.toISOString())
						setMessage({ type: 'danger', description: CODE_LOCKED_MESSAGE })
						return
					case 'expired':
						setCodeExpired(true)
						// A fresh code can go out now, whatever the last countdown said
						setNextResend(null)
						setMessage({ type: 'danger', description: CODE_EXPIRED_MESSAGE })
						return
					case 'rateLimited':
						setMessage({ type: 'danger', description: VERIFY_RATE_LIMITED_MESSAGE })
						return
					case 'error':
						setMessage({ type: 'danger', description: failure.message })
				}
			},
			isModal
		)
	}

	/** Back to the email step: the next code needs a new start for whatever address is entered. */
	const reset = () => {
		setEmail(null)
		setNextResend(null)
		setBlockedUntil(null)
		setRetryAt(null)
		setCodeExpired(false)
		setOutcome(null)
		setMessage(null)
	}

	return {
		email,
		nextResend,
		/** When a resend may next be sent: the later of the server's resend time and a 429's Retry-After */
		resendAt: laterOf(nextResend, retryAt),
		outcome,
		blockedUntil,
		retryAt,
		codeExpired,
		resetKey,
		sending,
		verifying,
		busy: sending || verifying,
		start,
		verify,
		reset,
	}
}
